import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, ShieldOff, ArrowLeft, Bell, Check, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';

export default function AdminOrderNotifications() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [allUsers, recs] = await Promise.all([
        base44.entities.User.list(),
        base44.entities.OrderNotificationRecipient.list(),
      ]);
      setUsers(allUsers || []);
      setRecipients(recs || []);
    } catch (e) {
      setUsers([]);
      setRecipients([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') loadData();
  }, [currentUser]);

  const adminUsers = users.filter(u => u.role === 'admin');
  const recipientMap = new Map(recipients.map(r => [r.user_id, r]));
  const adminUserIds = new Set(adminUsers.map(u => u.id));
  const standaloneRecipients = recipients.filter(r => !adminUserIds.has(r.user_id));

  const handleToggle = async (user) => {
    setTogglingId(user.id);
    const existing = recipientMap.get(user.id);
    try {
      if (existing) {
        await base44.entities.OrderNotificationRecipient.delete(existing.id);
      } else {
        await base44.entities.OrderNotificationRecipient.create({
          user_id: user.id,
          email: user.email,
          name: user.full_name || user.email,
          active: true,
        });
      }
      await loadData();
    } catch (e) {
      toast({ title: 'Could not update recipient', description: e.message, variant: 'destructive' });
      await loadData();
    }
    setTogglingId(null);
  };

  const handleToggleStandalone = async (recipient) => {
    setTogglingId(recipient.id);
    try {
      await base44.entities.OrderNotificationRecipient.update(recipient.id, { active: !recipient.active });
      await loadData();
    } catch (e) {
      toast({ title: 'Could not update recipient', description: e.message, variant: 'destructive' });
      await loadData();
    }
    setTogglingId(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">MARKETING &amp; COMMS</p>
            <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">NOTIFICATION RECIPIENTS</h1>
            <p className="text-white/30 text-sm mt-1">
              Choose which email addresses receive copies of every website order and group booking enquiry.
            </p>
            <p className="text-white/30 text-xs mt-2">
              Logged in as: <span className="text-white font-medium">{currentUser.first_name || currentUser.full_name?.split(' ')[0]} ({currentUser.email})</span>
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-10 max-w-3xl mx-auto">
        <div className="bg-white/[0.02] border border-white/5 p-5 mb-6 flex items-start gap-3">
          <Mail className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <p className="text-white/40 text-xs leading-relaxed">
            Recipients receive an email copy of every website order and every group booking enquiry (party / corporate). Toggle any address on or off below — only active recipients receive notifications.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {standaloneRecipients.length > 0 && (
              <div className="mb-8">
                <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3">EMAIL ADDRESSES</p>
                <div className="border border-white/5 divide-y divide-white/5">
                  {standaloneRecipients.map(r => (
                    <div key={r.id} className="flex items-center justify-between px-5 py-4">
                      <div className="min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{r.name || r.email}</p>
                        <p className="text-white/30 text-xs truncate">{r.email}</p>
                      </div>
                      <button
                        onClick={() => handleToggleStandalone(r)}
                        disabled={togglingId === r.id}
                        className={`flex items-center gap-2 px-4 py-2 text-[10px] font-bold tracking-wider transition-all disabled:opacity-40 ${
                          r.active
                            ? 'bg-primary/10 border border-primary/40 text-primary'
                            : 'bg-white/5 border border-white/10 text-white/50 hover:border-primary/40'
                        }`}
                      >
                        {togglingId === r.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : r.active ? (
                          <Check className="w-3.5 h-3.5" />
                        ) : (
                          <Bell className="w-3.5 h-3.5" />
                        )}
                        {r.active ? 'RECEIVING NOTIFICATIONS' : 'PAUSED'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3">ADMIN MEMBERS</p>
            {adminUsers.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-12">No admin members found.</p>
            ) : (
          <div className="border border-white/5 divide-y divide-white/5">
            {adminUsers.map(u => {
              const isOn = recipientMap.has(u.id);
              return (
                <div key={u.id} className="flex items-center justify-between px-5 py-4">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{u.full_name || u.email}</p>
                    <p className="text-white/30 text-xs truncate">{u.email}</p>
                  </div>
                  <button
                    onClick={() => handleToggle(u)}
                    disabled={togglingId === u.id}
                    className={`flex items-center gap-2 px-4 py-2 text-[10px] font-bold tracking-wider transition-all disabled:opacity-40 ${
                      isOn
                        ? 'bg-primary/10 border border-primary/40 text-primary'
                        : 'bg-white/5 border border-white/10 text-white/50 hover:border-primary/40'
                    }`}
                  >
                    {togglingId === u.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : isOn ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Bell className="w-3.5 h-3.5" />
                    )}
                    {isOn ? 'RECEIVING COPIES' : 'ADD TO COPIES'}
                  </button>
                </div>
              );
            })}
            </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}